import { useQuery } from '@apollo/client';
import { getLocalizedQueryVariables } from '@coursera-web/internationalization-lib';

import { useLocation } from 'js/lib/useRouter';
import user from 'js/lib/user';

import { SentryErrorType, captureExceptionInSentry } from 'bundles/campaigns/utils/sentryUtil';
import { FrontPageContentFragmentDoc } from 'bundles/front-page/__generated__/contentfulOperations';
import type {
  CareerAcademyModuleFragment,
  CompactCollectionsModuleFragment,
  DomainsModuleFragment,
  HeroBentoModuleFragment,
  OnboardingModuleFragment,
  PromoTemplateCannedCollectionFragment,
  PromoTemplateCollectionsFragment,
  PromoTemplateFaqsFragment,
  PromoTemplatePartnerListFragment,
  PromoTemplateTestimonialCarouselFragment,
} from 'bundles/front-page/__generated__/contentfulTypes';
import type { GetFrontPageVariantContentQuery } from 'bundles/front-page/queries/__generated__/getVariantContent';
import getFrontPageVariantContent from 'bundles/front-page/queries/getVariantContent.graphql';

export type Modules = Array<
  | HeroBentoModuleFragment
  | CompactCollectionsModuleFragment
  | CareerAcademyModuleFragment
  | DomainsModuleFragment
  | OnboardingModuleFragment
  | PromoTemplatePartnerListFragment
  | PromoTemplateCollectionsFragment
  | PromoTemplateTestimonialCarouselFragment
  | PromoTemplateCannedCollectionFragment
  | PromoTemplateFaqsFragment
>;

export type PropsFromGraphQL = {
  modules?: Modules;
  loading: boolean;
};

const usePageContentQuery = (): PropsFromGraphQL => {
  const location = useLocation();
  const previewParam = location?.query?.preview;
  const isPreview = !!user.isSuperuser() && (previewParam === 'true' || previewParam === '1');

  const frontPageContentLocalizedQueryVars = getLocalizedQueryVariables({
    isPreview,
    contentType: 'lohpPageContent',
    fragmentDoc: FrontPageContentFragmentDoc,
    fragmentFieldsName: 'FrontPageContent',
    contentfulSpace: 'marketing',
  });

  const frontPageContentLocalizedVariantQueryVars = {
    ...frontPageContentLocalizedQueryVars?.contentfulRequest?.variables,
    query: frontPageContentLocalizedQueryVars?.contentfulRequest?.query,
    epicNamespace: 'localization',
    contentfulSpace: 'marketing',
  };

  const { data, loading } = useQuery(getFrontPageVariantContent, {
    variables: frontPageContentLocalizedVariantQueryVars,
    context: { clientName: 'gatewayGql' },
    errorPolicy: 'all',
    ssr: true,
    onError: (error) => {
      captureExceptionInSentry({
        error,
        extra: {
          message: 'Error fetching front page content localized variant',
          isPreview,
        },
        errorType: SentryErrorType.GRAPHQL_ERROR,
      });
    },
  });

  const modules = (
    data as GetFrontPageVariantContentQuery
  )?.LocalizedData?.getVariantContent?.contentDataJson?.modulesCollection?.items?.filter(Boolean);

  return {
    modules,
    loading,
  };
};

export default usePageContentQuery;
